import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";

// Catálogo padrão de permissões (fallback)
export const ACTIONS = ["view", "create", "update", "delete"];

/** Recursos do menu principal — alinhado a `xpro4api/src/utils/permissionsCatalog.js` */
export const MENU_PERMISSIONS = [
  { key: "dashboard",        label: "Dashboard",                    actions: ["view"] },
  { key: "chat",             label: "Atendimento",                  actions: ["view"] },
  { key: "bpe",              label: "Emitir BPe (passagens rodoviárias)", actions: ["view"] },
  { key: "clients",          label: "Clientes",                     actions: ["view"] },
  { key: "awareness",        label: "Níveis de Consciência",        actions: ["view"] },
  { key: "sales_pipeline",   label: "Pipeline de Vendas",           actions: ["view"] },
  { key: "surveys",          label: "Pesquisas",                    actions: ACTIONS },
  { key: "campaigns",        label: "Campanhas",                    actions: ["view"] },
];

/** Recursos de Configurações — alinhado à API (fallback / matriz de cargos) */
export const SETTINGS_PERMISSIONS = [
  { key: "products",         label: "Produtos",                     actions: ACTIONS },
  { key: "products_stock",         label: "Estoque de Produtos",                     actions: ["update"] },
  { key: "products_price",         label: "Preço de Produtos",                     actions: ["update"] },
  { key: "cities",           label: "Cidades",                      actions: ["view"] },
  { key: "users",              label: "Usuários", actions: ACTIONS },
  { key: "chatbot",            label: "Chatbots", actions: ACTIONS },
  { key: "positions",          label: "Cargos", actions: ACTIONS },
  { key: "channels",           label: "Canais", actions: ACTIONS },
  { key: "types",              label: "Tipos", actions: ACTIONS },
  { key: "companies",          label: "Empresas", actions: ACTIONS },
  { key: "automation_rules",   label: "Regras de Automação", actions: ACTIONS },
  { key: "antifraud_methods",  label: "Métodos de Antifraude", actions: ACTIONS },
  { key: "payment_methods",    label: "Formas de Pagamento", actions: ACTIONS },
  { key: "client_tags",        label: "Tags de Cliente", actions: ACTIONS },
  { key: "billing",            label: "Billing e planos", actions: ACTIONS },
  { key: "delivery_methods",   label: "Formas de Entrega", actions: ACTIONS },
  { key: "categories",         label: "Categorias de Produtos", actions: ACTIONS },
  { key: "coupons",            label: "Cupons de Desconto", actions: ACTIONS },
  { key: "awareness_stages",   label: "Etapas dos Níveis de Consciência", actions: ACTIONS },
  { key: "banners",            label: "Banners", actions: ACTIONS },
  { key: "contents",           label: "Conteúdos", actions: ACTIONS },
  { key: "agents",             label: "Agentes", actions: ACTIONS },
  { key: "workspace_i18n",     label: "Idioma do workspace", actions: ["view", "update"] },
  { key: "integrations",       label: "Integrações", actions: ACTIONS },
  { key: "paid_ads",           label: "Anúncios (Meta / Google)", actions: ACTIONS },
];

/** Catálogo padrão usado pela AddPositionForm/PermissionMatrix */
export const DEFAULT_PERMISSION_CATALOG = [
  { module: "Menu",            resources: MENU_PERMISSIONS },
  { module: "Configurações",   resources: SETTINGS_PERMISSIONS },
];

export const usePositions = (page = 1, limit = 100, search = "") => {
  const queryClient = useQueryClient();

  const query = useQuery(
    ["positions", page, limit, search],
    () => fetchData(`/positions?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
    {
      staleTime: 5000,
      refetchOnWindowFocus: false,
      select: (response) => ({
        positions: response?.data || [],
        pagination: response?.pagination || { total: 0 },
      }),
    }
  );

  const updateMutation = useMutation(
    (position) => {
      const method = position?.id ? "PUT" : "POST";
      const endpoint = position?.id ? `/positions/${position.id}` : `/positions`;
      return fetchData(endpoint, method, position);
    },
    { onSuccess: () => queryClient.invalidateQueries("positions") }
  );

  const deleteMutation = useMutation(
    (id) => fetchData(`/positions/${id}`, "DELETE"),
    { onSuccess: () => queryClient.invalidateQueries("positions") }
  );

  return { ...query, updateMutation, deleteMutation };
};

export const usePermissionCatalog = () => {
  const query = useQuery(
    ["permissionCatalog"],
    async () => {
      try {
        // Backend expõe o catálogo
        const res = await fetchData(`/permissions/catalog`, "GET");
        // Esperado: [{ module, resources: [{key,label,actions?}]}]
        if (Array.isArray(res?.data) && res.data.length) return res.data;
        return DEFAULT_PERMISSION_CATALOG;
      } catch {
        return DEFAULT_PERMISSION_CATALOG;
      }
    },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false }
  );

  return { catalog: query.data || DEFAULT_PERMISSION_CATALOG, isLoading: query.isLoading, error: query.error };
};
